import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Flow, IntegrationType, Theme } from '../@types/config'
import { mapFromWizardFlowTypeToFlow, WizardFlowType } from '../@types/wizardFlowType'
import { useWizardFlow } from '../hooks'
import {
  updateCoopId,
  updateEnableFrontendTracking,
  updateIntegrationType,
  updateFlow,
  updateIframe,
  updateStatus,
  updateTealium,
  updateTheme,
  updateWordings,
} from '../store/config/configSlice'
import { updateWizardFlowType } from '../store/flow/flowSlice'
import { generateRGBAString } from '../utils/test-utils'

const getWizardFlowType = (flow: Flow): WizardFlowType => {
  return flow === 'ibw' ? WizardFlowType.IBW : WizardFlowType.RLE
}

const useConfig = (): void => {
  const dispatch = useDispatch()
  const initialFlowType = useWizardFlow()
  const dispatchModifiedFlowType = (flow: Flow): void => {
    dispatch(updateFlow(flow as Flow))
    const flowType = ['rle', 'ibw'].includes(flow) ? getWizardFlowType(flow as Flow) : initialFlowType
    dispatch(updateWizardFlowType(flowType))
  }

  const dispatchInitialFlowType = () => {
    dispatch(updateWizardFlowType(initialFlowType))
    dispatch(updateFlow(mapFromWizardFlowTypeToFlow(initialFlowType)))
  }

  const dispatchSolarConfig = () => {
    dispatch(updateIframe(false))
    dispatch(updateTealium(true))
    dispatch(updateIntegrationType(IntegrationType.IMMOBILIENSCOUT24))
    dispatch(updateEnableFrontendTracking(true))
  }

  useEffect(() => {
    if (initialFlowType === WizardFlowType.SOLAR) {
      dispatchInitialFlowType()
      dispatchSolarConfig()
      return
    }
    const configurationData = window.IS24?.RLE?.configuration

    if (!configurationData) {
      dispatchInitialFlowType()
      return
    }
    const {
      cooperationId,
      colors,
      flow,
      isIframe,
      status,
      tealium,
      wordings,
      integrationType,
      enableFrontendTracking,
    } = configurationData
    const theme = colors
      ? ({
          ...colors,
          primaryLight: colors?.primary ? generateRGBAString(colors.primary, 10) : undefined,
          secondaryLight: colors?.secondary ? generateRGBAString(colors.secondary, 50) : undefined,
        } as Theme)
      : null
    if (flow) {
      dispatchModifiedFlowType(flow.toLowerCase() as Flow)
    } else {
      dispatchInitialFlowType()
    }
    if (cooperationId) dispatch(updateCoopId(cooperationId))
    if (isIframe) dispatch(updateIframe(isIframe))
    if (tealium !== undefined) dispatch(updateTealium(tealium))
    if (theme) dispatch(updateTheme(theme))
    if (status) dispatch(updateStatus(status))
    if (wordings) dispatch(updateWordings(wordings))
    if (integrationType !== undefined) dispatch(updateIntegrationType(integrationType))
    if (enableFrontendTracking !== undefined) dispatch(updateEnableFrontendTracking(enableFrontendTracking))
  }, [])
}

export { useConfig }
